import React, { useCallback, memo } from 'react';
import { useDispatch } from 'react-redux';
import * as S from './styles';
import {
  UserAlert,
  IUserAlertBl,
  IUserAlertCompany,
} from 'interfaces/user-alert';
import { useTranslation } from 'hooks';
import { translations } from './translations';
import { DeleteUserAlertActions } from 'store/ducks/settings/user-alerts';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Info, MinusCircle } from 'lucide-react';
import { cn } from 'lib/utils';
interface IGridUserAlertsProps {
  userAlerts: UserAlert[];
  loading: boolean;
  onDelete: () => void;
  extraColumns: boolean;
}

interface IUserAlertProps {
  userAlert: UserAlert;
  onDelete: () => void;
  extraColumns: boolean;
}

// Popover reutilizável para listas (companies / business lines)
const ListPopover = memo(
  ({ items, title }: { items: string[]; title: string }) => (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={items.length === 0}
          className={cn(
            'flex items-center gap-1 text-sm font-medium transition',
            items.length > 0
              ? 'cursor-pointer text-primary-600 hover:opacity-80'
              : 'cursor-default text-gray-400'
          )}
        >
          {items.length > 0 ? (
            <>
              <span>{items.length}</span>
              <Info size={14} />
            </>
          ) : (
            <>
              <span>N/A</span>
              <MinusCircle size={14} />
            </>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-64 p-4 rounded-xl shadow-lg border bg-white z-50">
        <h3 className="mb-2 text-base font-semibold">{title}</h3>

        <div className="max-h-48 overflow-y-auto pr-2">
          {items.length > 0 ? (
            <ul className="space-y-2">
              {items.map((item, index) => (
                <li
                  key={index}
                  className="text-sm text-gray-700 border-b last:border-none pb-1"
                >
                  {item}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-gray-500 italic">N/A</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
);

const Item: React.FC<IUserAlertProps> = ({
  userAlert,
  onDelete,
  extraColumns,
}) => {
  const dispatch = useDispatch();
  const { getTranslation } = useTranslation(translations);

  const handleDelete = useCallback(() => {
    dispatch(DeleteUserAlertActions.request(userAlert.id, onDelete));
  }, [dispatch, userAlert.id, onDelete]);

  const companies = userAlert.userAlertCompanies.map(
    (el: IUserAlertCompany) => el.company.trade_name
  );

  const businessLines = userAlert.userAlertBls.map(
    (el: IUserAlertBl) =>
      `${el.businessLine.activity_division} - ${el.businessLine.description}`
  );

  return (
    <S.ItemContainer>
      <S.ItemContent extraColumns={extraColumns}>
        <S.ItemValue>{userAlert.user.name || 'N/A'}</S.ItemValue>
        <S.ItemValue>{userAlert.user.email || 'N/A'}</S.ItemValue>

        {extraColumns && (
          <>
            <S.ItemValue>
              <ListPopover items={companies} title={getTranslation('centro')} />
            </S.ItemValue>

            <S.ItemValue>
              <ListPopover
                items={businessLines}
                title={getTranslation('businessLine')}
              />
            </S.ItemValue>
          </>
        )}

        <S.Button btStyle="danger" type="button" onClick={handleDelete}>
          {getTranslation('deletar')}
        </S.Button>
      </S.ItemContent>
    </S.ItemContainer>
  );
};

export const GridUserAlerts: React.FC<IGridUserAlertsProps> = ({
  userAlerts = [],
  loading = false,
  onDelete,
  extraColumns,
}) => {
  const { getTranslation } = useTranslation(translations);

  if (loading) {
    return (
      <S.Container>
        <S.Loading />
      </S.Container>
    );
  }

  if (userAlerts.length === 0) {
    return <S.Container />;
  }

  return (
    <S.Container>
      <S.Header extraColumns={extraColumns}>
        <S.Label>{getTranslation('nome')}</S.Label>
        <S.Label>{getTranslation('email')}</S.Label>
        {extraColumns && (
          <>
            <S.Label>{getTranslation('centro')}</S.Label>
            <S.Label>{getTranslation('businessLine')}</S.Label>
          </>
        )}
      </S.Header>

      {userAlerts.map((userAlert) => (
        <Item
          key={userAlert.id}
          userAlert={userAlert}
          onDelete={onDelete}
          extraColumns={extraColumns}
        />
      ))}
    </S.Container>
  );
};
